import {Link} from '@primer/react'
import {Fragment} from 'react'

// Maximum length for main URL text fragment in the punctuation regex.
// This non-greedy limit prevents catastrophic backtracking on strings with many repeated symbols.
// See: https://github.com/github/github-ui/security/code-scanning/556
const MAX_FRAGMENT_LENGTH = 1000

// Trailing punctuation characters that should be stripped before URL validation.
// Leading punctuation (open brackets/quotes) is also stripped.
const PUNCTUATION_REGEX = new RegExp(`^([(['"]{0,3})(.{1,${MAX_FRAGMENT_LENGTH}}?)([.,:;!?\\])"']{0,10})$`)

function cleanPunctuation(text: string): {cleanText: string; prefix: string; suffix: string} {
  const match = text.match(PUNCTUATION_REGEX)
  if (!match) {
    return {cleanText: text, prefix: '', suffix: ''}
  }
  const [_fullMatch, prefix = '', cleanText = '', suffix = ''] = match
  return {cleanText, prefix, suffix}
}

/**
 * Checks if a string is a valid http or https URL.
 * Safe to call during SSR since we avoid browser-only APIs.
 */
function isValidHttpUrl(url: string): boolean {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return false
  }
  try {
    // The URL is guaranteed to be absolute (http/https prefix checked above),
    // so no base is required. eslint-disable is needed because the rule
    // requires window.location.origin as base, but window is not available in SSR.
    // eslint-disable-next-line no-restricted-syntax
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Renders a string with any http/https URLs converted to clickable links.
 * Non-URL text and whitespace are rendered as plain text fragments.
 * Returns null for empty input.
 *
 * SSR-safe: renders the same structure on server and client to avoid hydration mismatches.
 */
export function renderTextWithLinks(text: string): React.ReactElement | null {
  if (!text) {
    return null
  }

  const fragments = text.split(/(\s+)/)

  return (
    <>
      {fragments.map((fragment, index) => {
        if (!fragment.trim()) {
          return <Fragment key={index}>{fragment}</Fragment>
        }

        const {cleanText, prefix, suffix} = cleanPunctuation(fragment)

        if (!cleanText) {
          return <Fragment key={index}>{fragment}</Fragment>
        }

        if (isValidHttpUrl(cleanText)) {
          return (
            <Fragment key={index}>
              {prefix}
              <Link href={cleanText} target="_blank" rel="noopener noreferrer" inline>
                {cleanText}
              </Link>
              {suffix}
            </Fragment>
          )
        }

        return <Fragment key={index}>{fragment}</Fragment>
      })}
    </>
  )
}
